import { dayTypeFor } from './worked-hours-split'
import type { PublicHolidayInfo } from './leave-calculations'
import { facturatieAfspraakGeldt, type PauzeVerloning } from './werkbon-pauze'

export interface DayEntryInput {
  plannedStart: string
  plannedEnd: string
  actualStart?: string | null
  actualEnd?: string | null
  /** Feitelijke pauze in minuten. */
  pauzeMinutes: number
  /**
   * FO-473 - gaat de pauze van de BETAALDE uren af? VERPLICHT, zodat geen
   * enkel pad de verloningsas per ongeluk overslaat: DOORBETAALD = niet
   * aftrekken, GESCHREVEN = wel. `null` = een dagregel van vóór FO-473 en
   * gedraagt zich als GESCHREVEN (het historische gedrag).
   */
  pauzeVerloning: PauzeVerloning | null
}

/** FO-473 - `DayEntryInput` van vóór de tweeassige pauze (verloning optioneel). */
export interface DayEntryInputLegacy extends Omit<DayEntryInput, 'pauzeVerloning'> {
  pauzeVerloning?: PauzeVerloning | null
}

/**
 * FO-473 - de minuten die de pauze van de BETAALDE uren aftrekt: de volle
 * pauze bij GESCHREVEN (of onbekend), niets bij DOORBETAALD.
 */
export function verloningsAftrekMinuten(
  pauzeMinutes: number | null | undefined,
  pauzeVerloning: PauzeVerloning | null | undefined,
): number {
  if (typeof pauzeMinutes !== 'number' || !Number.isFinite(pauzeMinutes) || pauzeMinutes <= 0) {
    return 0
  }
  return pauzeVerloning === 'DOORBETAALD' ? 0 : pauzeMinutes
}

function toMinutes(hhmm: string): number {
  const m = /^(\d{2}):(\d{2})$/.exec(hhmm)
  if (!m) throw new Error(`Ongeldige tijd: "${hhmm}"`)
  const h = parseInt(m[1], 10)
  const min = parseInt(m[2], 10)
  if (h > 23 || min > 59) throw new Error(`Ongeldige tijd: "${hhmm}"`)
  return h * 60 + min
}

/**
 * Format een decimaal urenwaarde naar "H:mm" tekst.
 *
 * 0      → "0:00"
 * 0.75   → "0:45"
 * 1.25   → "1:15"
 * 12.5   → "12:30"
 *
 * Negatieve waarden klampen op 0 (geen "minus"-tijden). Minuten worden
 * afgerond naar de dichtstbijzijnde gehele minuut. Bedoeld voor UI-weergave
 * in plaats van Nederlandse komma-notatie ("0,75 u") — die geeft monteurs
 * te vaak het verkeerde idee dat ze decimale uren moeten invullen.
 */
export function formatHoursAsHHMM(uren: number | null | undefined): string {
  if (uren == null || !Number.isFinite(uren) || uren <= 0) return '0:00'
  const totalMinutes = Math.round(uren * 60)
  const h = Math.floor(totalMinutes / 60)
  const m = totalMinutes % 60
  return `${h}:${m.toString().padStart(2, '0')}`
}

/**
 * Format een decimaal urenwaarde naar mens-leesbare korte tekst.
 *
 *   0       → "0min"
 *   0.25    → "15min"
 *   0.75    → "45min"
 *   1       → "1u"
 *   1.25    → "1u 15min"
 *   8       → "8u"
 *   12.5    → "12u 30min"
 *
 * In contrast met `formatHoursAsHHMM` (notatie "1:15") is dit het
 * primaire display-format voor planning + planbord — monteurs lazen "1:15"
 * af als klok-tijdstip i.p.v. duur. "1u 15min" is ondubbelzinnig duur.
 *
 * Minuten worden afgerond op de dichtstbijzijnde gehele minuut. Negatieve
 * of niet-eindige waarden klampen op "0min".
 */
export function formatHoursHuman(uren: number | null | undefined): string {
  if (uren == null || !Number.isFinite(uren) || uren <= 0) return '0min'
  const totalMinutes = Math.round(uren * 60)
  const h = Math.floor(totalMinutes / 60)
  const m = totalMinutes % 60
  if (h === 0) return `${m}min`
  if (m === 0) return `${h}u`
  return `${h}u ${m}min`
}

/**
 * Parse "HH:mm" tekst (24-uurs) naar het aantal minuten sinds middernacht.
 * Returnt `null` bij ongeldige input. Niet-strict op leading zero (`8:30` → 510).
 */
export function parseHHmmToMinutes(hhmm: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim())
  if (!m) return null
  const h = parseInt(m[1], 10)
  const min = parseInt(m[2], 10)
  if (h < 0 || h > 23 || min < 0 || min > 59) return null
  return h * 60 + min
}

/**
 * Parse een duur-string in `H:mm`-format naar decimale uren.
 *
 *   "0:45"   → 0.75
 *   "1:30"   → 1.5
 *   "12:00"  → 12
 *   "0"      → 0    (afsluitende `:mm` is optioneel zodat een monteur snel
 *                    een heel uur kan invullen zonder dubbelpunt)
 *
 * Verschilt van `durationHoursBetween` doordat de input een duur is, niet
 * twee tijdstippen. Accepteert minuten 0-59 (geen klok-tijdstip-validatie:
 * uren-deel mag groot zijn voor lange ritten, max 23 om typo's te vangen).
 */
export function parseHHmmDurationToHours(input: string): number | null {
  const trimmed = input.trim()
  if (trimmed === '') return null
  // Sta "0" / "1" toe — direct als heel uur, voor toetsenbord-snelheid.
  if (/^\d{1,2}$/.test(trimmed)) {
    const h = parseInt(trimmed, 10)
    if (h < 0 || h > 23) return null
    return h
  }
  const m = /^(\d{1,2}):(\d{2})$/.exec(trimmed)
  if (!m) return null
  const h = parseInt(m[1], 10)
  const min = parseInt(m[2], 10)
  if (h < 0 || h > 23 || min < 0 || min > 59) return null
  return h + min / 60
}

/**
 * Bereken duur in uren (decimaal) tussen twee `HH:mm`-tijdstippen. Behandelt
 * een nacht-overgang (eind < start) door 24u op te tellen. Bij ongeldige
 * input retourneert `null`.
 */
export function durationHoursBetween(startHHmm: string, endHHmm: string): number | null {
  const s = parseHHmmToMinutes(startHHmm)
  const e = parseHHmmToMinutes(endHHmm)
  if (s == null || e == null) return null
  let diff = e - s
  if (diff < 0) diff += 24 * 60
  return diff / 60
}

/**
 * Snap een aantal minuten naar het dichtstbijzijnde kwartier (0/15/30/45/…).
 *
 *   23 → 30, 7 → 0, 0 → 0, 45 → 45
 *
 * De werkbon-API dwingt 15-minuten-granulariteit af (`isMultipleOf15`) als
 * harde billing/payroll-regel. De portal-inputs (pauze, reisminuten) laten
 * fijnere waarden toe; door client-side te snappen ziet de monteur meteen de
 * waarde die ook daadwerkelijk wordt verstuurd. Negatief/niet-eindig → 0.
 */
export function snapMinutesTo15(value: number | null | undefined): number {
  if (value == null || !Number.isFinite(value) || value <= 0) return 0
  return Math.round(value / 15) * 15
}

/**
 * Snap een `HH:mm`-tijdstring naar het dichtstbijzijnde kwartier.
 *
 *   "08:23" → "08:30", "08:07" → "08:00", "08:00" → "08:00", "08:53" → "09:00"
 *
 * Spiegelt de werkbon-API `time15Schema` (alleen 00/15/30/45 toegestaan). Een
 * native `<input type="time">` levert op mobiel 1-minuut-stappen; deze helper
 * lijnt de waarde zichtbaar uit op het kwartier-grid. Snapt naar 60 min → het
 * volgende hele uur (23:53 → 24:00 → 00:00, blijft binnen de dag-modulus).
 * Lege/ongeldige input wordt ongemoeid teruggegeven (de aanroeper bepaalt of
 * een leeg veld toegestaan is).
 */
export function snapTimeTo15(time: string | null | undefined): string {
  if (!time) return time ?? ''
  const minutes = parseHHmmToMinutes(time)
  if (minutes == null) return time
  const snapped = (Math.round(minutes / 15) * 15) % (24 * 60)
  const h = Math.floor(snapped / 60)
  const m = snapped % 60
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`
}

function round2(n: number): number {
  return Math.round(n * 100) / 100
}

/**
 * FO-473 - de BETAALDE uren van een dagregel (de verloningsas; dit is wat in
 * `WerkbonDayEntry.hours` staat en wat weekstaat en verloning lezen).
 *
 * Effectieve tijden = actuals wanneer beide gezet zijn, anders de planning;
 * een dienst over middernacht telt door naar de volgende dag. De pauze gaat er
 * alleen af als hij GESCHREVEN is: een DOORBETAALDE pauze telt mee. Een
 * factuurpauze speelt hier bewust geen rol - die raakt de betaalde uren nooit.
 */
export function betaaldeUren(entry: DayEntryInput): number {
  const useActual = entry.actualStart != null && entry.actualEnd != null
  const start = useActual ? entry.actualStart! : entry.plannedStart
  const end   = useActual ? entry.actualEnd!   : entry.plannedEnd
  const startMin = toMinutes(start)
  let endMin = toMinutes(end)
  if (endMin < startMin) endMin += 24 * 60 // nachtdienst
  const minutes =
    endMin - startMin - verloningsAftrekMinuten(entry.pauzeMinutes, entry.pauzeVerloning)
  const hours = Math.max(0, minutes) / 60
  return round2(hours)
}

/**
 * @deprecated FO-473 - gebruik `betaaldeUren` (verloning verplicht). Blijft
 * bestaan voor code van vóór de tweeassige pauze; zonder `pauzeVerloning`
 * rekent hij zoals altijd (pauze gaat eraf). De drift-guard
 * `werkbon-pauze-drift.test.ts` weigert nieuwe aanroepen in de api.
 */
export function computeDayEntryHours(entry: DayEntryInputLegacy): number {
  return betaaldeUren({ ...entry, pauzeVerloning: entry.pauzeVerloning ?? null })
}

/** Invoer van `facturabeleUren`: de opgeslagen dagregel-velden. */
export interface FacturabeleUrenInput {
  /** De opgeslagen BETAALDE uren (`WerkbonDayEntry.hours`). */
  hours: number
  pauzeMinutes?: number | null
  pauzeVerloning?: PauzeVerloning | null
  /**
   * Minuten pauze op de factuur. `null`/`undefined` = een dagregel van vóór
   * FO-473 (of een pad dat de kolom niet vult): de factuur volgt dan de
   * betaalde uren, minus de oude schema-brede facturatie-pauze.
   */
  pauzeFacturatieMinuten?: number | null
  /** Werkdatum; nodig voor de ingangsdatum van de oude facturatie-pauze. */
  date?: Date | string | null
}

/** Schema-brede facturatie-pauze van vóór FO-473 (`ClientWorkSchedule.facturatiePauze*`). */
export interface OudeFacturatiePauze {
  minuten: number
  /** Eerste werkdatum waarop de aftrek gold; `null` = altijd. */
  vanaf: Date | null
}

/**
 * FO-473 - de FACTURABELE uren van een dagregel (de facturatie-as).
 *
 * Gerekend vanaf de opgeslagen betaalde uren, zodat werksegmenten, oproepen en
 * handmatige correcties automatisch meetellen: tel de pauze die van de
 * betaalde uren afging weer op en trek de factuurpauze af. Het verschil tussen
 * betaald en gefactureerd is daarmee precies het verschil in pauze-aftrek.
 *
 * Een dag zonder uren (afwezig, leeg standby-slot, 0-uren-rij) blijft
 * ongemoeid en wordt nooit negatief; de uitkomst klemt op 0 en is op 2
 * decimalen afgerond, net als de rest van de facturatie-keten.
 */
export function facturabeleUren(
  entry: FacturabeleUrenInput,
  oudeFacturatiePauze?: OudeFacturatiePauze | null,
): number {
  const hours = Number(entry.hours)
  if (!Number.isFinite(hours)) return 0
  if (hours <= 0) return hours
  const factuur = entry.pauzeFacturatieMinuten
  if (typeof factuur !== 'number' || !Number.isFinite(factuur) || factuur < 0) {
    const oud = oudeFacturatiePauze ?? null
    if (!oud || !(oud.minuten > 0)) return hours
    if (!facturatieAfspraakGeldt(entry.date ?? null, oud.vanaf)) return hours
    return Math.max(0, round2(hours - oud.minuten / 60))
  }
  const aftrekBetaald = verloningsAftrekMinuten(entry.pauzeMinutes, entry.pauzeVerloning)
  return Math.max(0, round2(hours + (aftrekBetaald - factuur) / 60))
}

/**
 * De factuurpauze van een dagregel zoals de klant hem ziet: de eigen waarde,
 * of (van vóór FO-473) de pauze die uit de verloningsregel volgt.
 */
export function factuurPauzeMinutenVan(entry: {
  pauzeMinutes?: number | null
  pauzeVerloning?: PauzeVerloning | null
  pauzeFacturatieMinuten?: number | null
}): number {
  const eigen = entry.pauzeFacturatieMinuten
  if (typeof eigen === 'number' && Number.isFinite(eigen) && eigen >= 0) return eigen
  return verloningsAftrekMinuten(entry.pauzeMinutes, entry.pauzeVerloning)
}

function rangeMinutes(startHHmm: string | null | undefined, endHHmm: string | null | undefined): number {
  if (!startHHmm || !endHHmm) return 0
  const startMin = toMinutes(startHHmm)
  let endMin = toMinutes(endHHmm)
  if (endMin < startMin) endMin += 24 * 60
  return Math.max(0, endMin - startMin)
}

export interface WerkSegment {
  start: string
  end: string
  pauzeMinutes?: number
}

export interface ReisSegment {
  start: string
  end: string
}

/**
 * Som van alle werksegmenten, in uren, 2 decimalen. De pauze per segment gaat
 * er (net als bij `betaaldeUren`) alleen af als de pauze GESCHREVEN is; zonder
 * verloning = het historische gedrag (aftrekken).
 */
export function computeWerkHoursFromSegments(
  segments: WerkSegment[],
  pauzeVerloning?: PauzeVerloning | null,
): number {
  let totalMinutes = 0
  for (const s of segments) {
    const minutes =
      rangeMinutes(s.start, s.end) - verloningsAftrekMinuten(s.pauzeMinutes ?? 0, pauzeVerloning)
    totalMinutes += Math.max(0, minutes)
  }
  return round2(totalMinutes / 60)
}

/** Som van alle reissegmenten in minuten (heel getal). */
export function computeReisMinutesFromSegments(segments: ReisSegment[]): number {
  let total = 0
  for (const s of segments) total += rangeMinutes(s.start, s.end)
  return total
}

export interface OverurenInput {
  plannedStart: string
  plannedEnd: string
  pauzeMinutes: number
  /**
   * FO-473 - verplicht: de geplande uren moeten op dezelfde as rekenen als de
   * betaalde uren. Anders telt een doorbetaalde pauze elke dag als "overuur".
   */
  pauzeVerloning: PauzeVerloning | null
  hours: number
}

/**
 * Overuren = werkelijke (betaalde) uren - geplande betaalde uren.
 * Minimum 0. Afgerond op 2 decimalen.
 */
export function computeOveruren(input: OverurenInput): number {
  const plannedHours = betaaldeUren({
    plannedStart: input.plannedStart,
    plannedEnd: input.plannedEnd,
    pauzeMinutes: input.pauzeMinutes,
    pauzeVerloning: input.pauzeVerloning,
    actualStart: null,
    actualEnd: null,
  })
  const delta = Math.max(0, input.hours - plannedHours)
  return Math.round(delta * 100) / 100
}

// ─── FO-066: dienstsoort-/dagType-bewuste per-dag overuren ───────────────────

/** Ploegensysteem van de werkbon (spiegelt de Prisma `DienstType`-enum). */
export type DagOverurenDienstType =
  | 'DAGDIENST'
  | 'PLOEGEN_2'
  | 'PLOEGEN_3'
  | 'PLOEGEN_4'
  | 'PLOEGEN_5'
  | 'CONSIGNATIE'

/** Ploegsoort van de werkbon (spiegelt de Prisma `PloegSoort`-enum). */
export type DagOverurenPloegSoort = 'DAG' | 'VROEG' | 'LAAT' | 'NACHT'

export interface DagOverurenInput extends OverurenInput {
  /** Kalenderdag van de dag-entry (UTC-dag of ISO `yyyy-MM-dd`) — bron voor weekend/feestdag. */
  date: Date | string
  /** Werkelijke tijden (winnen van planned als beide gezet) — bepalen de "extra" uren. */
  actualStart?: string | null
  actualEnd?: string | null
}

export interface DagOverurenOpts {
  dienstType: DagOverurenDienstType | null
  ploegSoort: DagOverurenPloegSoort | null
  /** Feestdagen voor de FEESTDAG-afleiding; weglaten = alleen ZATERDAG/ZONDAG (kalender, geen DB). */
  feestdagen?: PublicHolidayInfo[]
  /** Standaard dagvenster-begin (CAO-basis `08:00`); uren ervóór zijn vroeg-/toeslag-uren. */
  dagVensterStart?: string
  /** Standaard dagvenster-eind (CAO-basis `17:00`); uren erna zijn avond-/nacht-/toeslag-uren. */
  dagVensterEind?: string
}

const DAG_VENSTER_START_DEFAULT = '08:00'
const DAG_VENSTER_EIND_DEFAULT = '17:00'
const PLOEGEN_ALLIN: readonly DagOverurenDienstType[] = ['PLOEGEN_3', 'PLOEGEN_4', 'PLOEGEN_5']

/** Parse `HH:mm` → minuten sinds middernacht; `null` bij ongeldige/lege input. */
function safeMinutesHHmm(hhmm: string | null | undefined): number | null {
  if (!hhmm) return null
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim())
  if (!m) return null
  const h = parseInt(m[1]!, 10)
  const min = parseInt(m[2]!, 10)
  if (h < 0 || h > 23 || min < 0 || min > 59) return null
  return h * 60 + min
}

function toUtcDayStart(date: Date | string): Date {
  const d = typeof date === 'string' ? new Date(`${date}T00:00:00.000Z`) : date
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()))
}

/**
 * Werkminuten boven de planning die BINNEN het standaard dagvenster [start, eind)
 * vallen, in uren. Effectieve werktijd = actuals indien beide gezet, anders
 * planned. "Extra" = werkinterval minus planinterval (vroege start + late
 * uitloop). Uren vóór `start` (vroeg) of na `eind` (avond/nacht) tellen niet —
 * die horen tot de toeslag-engine, niet tot de per-dag overuren. Pure functie.
 */
function extraWerkdagVensterUren(
  input: DagOverurenInput,
  venster: { start: string; eind: string },
): number {
  const useActual = input.actualStart != null && input.actualEnd != null
  const wS = safeMinutesHHmm(useActual ? input.actualStart : input.plannedStart)
  const wEraw = safeMinutesHHmm(useActual ? input.actualEnd : input.plannedEnd)
  const pS = safeMinutesHHmm(input.plannedStart)
  const pEraw = safeMinutesHHmm(input.plannedEnd)
  if (wS == null || wEraw == null || pS == null || pEraw == null) return 0

  const wE = wEraw < wS ? wEraw + 24 * 60 : wEraw // werk over middernacht
  const pE = pEraw < pS ? pEraw + 24 * 60 : pEraw // plan over middernacht
  const winStart = safeMinutesHHmm(venster.start)
  const winEind = safeMinutesHHmm(venster.eind)
  if (winStart == null || winEind == null || winEind <= winStart) return 0

  // Extra-intervallen t.o.v. de planning: vroege start [wS, pS) en late uitloop (pE, wE].
  const extra: Array<[number, number]> = []
  if (wS < pS) extra.push([wS, Math.min(pS, wE)])
  if (wE > pE) extra.push([Math.max(pE, wS), wE])

  let inWindowMin = 0
  for (const [a, b] of extra) {
    // Clamp op het dagvenster van dag 0: vroeg/avond/nacht én over-middernacht
    // (>= 24:00) valt buiten [winStart, winEind] en telt dus niet mee.
    const lo = Math.max(a, winStart)
    const hi = Math.min(b, winEind)
    if (hi > lo) inWindowMin += hi - lo
  }
  return inWindowMin / 60
}

/**
 * FO-066 — dienstsoort-/dagType-bewuste per-dag overuren.
 *
 * Het legacy per-dag getal (`computeOveruren` = werkelijk − gepland) is
 * dienstsoort- én dagType-blind: weekend-/nachturen verschenen onterecht als
 * overuren. Deze functie corrigeert dat met de werkbon-`dienstType`/`ploegSoort`:
 *
 *  - **CONSIGNATIE**: ongewijzigd (eigen consignatie-tarieven, buiten deze regel).
 *  - **PLOEGEN_3/4/5**: 0 — weekend én nacht zitten in de vaste all-in
 *    ploegentoeslag (Variant C); echte overuren zijn week-net.
 *  - **DAGDIENST / PLOEGEN_2**:
 *      - VROEG/LAAT/NACHT-dienst → 0: de dienst krijgt zijn ploegSoort-toeslag
 *        via de toeslag-engine, niet als per-dag overuren.
 *      - DAG-dienst → alleen de "extra" werkuren (boven de planning) die op een
 *        WERKDAG én BINNEN het standaard dagvenster vallen. Weekend
 *        (zaterdag/zondag/feestdag) → weekendtoeslag (0); uren vóór/na het
 *        dagvenster (vroeg/avond/nacht) → toeslag (0).
 *
 * Echte overuren (boven de week-norm, ATV-bewust) blijven volledig de week-net
 * `berekenUrenSplitsing`/`berekenWeekOverwerkEnMinuren` — die is de enige
 * loon-autoriteit. Dit per-dag getal is een weergave-/snapshot-veld en wordt
 * nooit groter dan het legacy verschil. Geen dubbeltelling: een uur is óf een
 * (week-net) overuur óf weekend-/nacht-toeslag, nooit beide in dezelfde
 * hoedanigheid. Puur + Decimal-vrij, dus unit-testbaar.
 */
export function computeDagOveruren(input: DagOverurenInput, opts: DagOverurenOpts): number {
  const legacy = computeOveruren(input)
  if (legacy <= 0) return 0

  const dienstType = opts.dienstType ?? 'DAGDIENST'
  // Consignatie houdt zijn eigen regels; ongewijzigd t.o.v. computeOveruren.
  if (dienstType === 'CONSIGNATIE') return legacy
  // 3-/4-/5-ploeg: weekend + nacht zit in de vaste ploegentoeslag → 0 per-dag overuren.
  if (PLOEGEN_ALLIN.includes(dienstType)) return 0

  // DAGDIENST / PLOEGEN_2.
  const ploeg = opts.ploegSoort ?? null
  // Vroeg-/laat-/nachtdienst krijgt zijn ploegSoort-toeslag via de engine — geen overuren.
  if (ploeg === 'VROEG' || ploeg === 'LAAT' || ploeg === 'NACHT') return 0

  // DAG-dienst (of geen ploegSoort): weekend → weekendtoeslag, geen per-dag overuren.
  const dagType = dayTypeFor(toUtcDayStart(input.date), opts.feestdagen)
  if (dagType !== 'WERKDAG') return 0

  // Alleen de extra werkuren binnen het standaard dagvenster; nooit méér dan het
  // legacy per-dag verschil (dat is de absolute bovengrens).
  const inWindow = extraWerkdagVensterUren(input, {
    start: opts.dagVensterStart ?? DAG_VENSTER_START_DEFAULT,
    eind: opts.dagVensterEind ?? DAG_VENSTER_EIND_DEFAULT,
  })
  return Math.round(Math.min(legacy, inWindow) * 100) / 100
}
